import React, { useState, useEffect, useRef } from "react";
import MiniLoader from "../MiniLoader";
// Fallback high-quality atmospheric travel images in case Unsplash API is rate-limited or lacks key
const FALLBACK_IMAGES = [
  {
    urls: {
      regular: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80",
      small: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80",
    },
    alt_description: "Scenic coast sunset",
    user: { name: "Sean Oulashin" }
  },
  {
    urls: {
      regular: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200&q=80",
      small: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=600&q=80",
    },
    alt_description: "Travel editorial dock",
    user: { name: "Paul Gilmore" }
  },
  {
    urls: {
      regular: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1200&q=80",
      small: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&q=80",
    },
    alt_description: "Moody valley",
    user: { name: "Bailey Zindel" }
  },
  {
    urls: {
      regular: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&q=80",
      small: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&q=80",
    },
    alt_description: "Road trip canyon",
    user: { name: "John Fowler" }
  }
];

const VIBE_CHIPS = ["All", "Food", "Nature", "Nightlife", "Culture", "Relaxed"];

// 15-20 Curated Cinematic Editorial Travel Tones
// const MUTED_TRAVEL_TONES = [
//   "#606c38", // Olive Green
//   "#dda15e", // Warm Beige
//   "#e9c46a", // Muted Amber
//   "#e76f51", // Faded Terracotta
//   "#2a9d8f", // Dusty Teal
//   "#a8dadc", // Mist Gray
//   "#f4a261", // Sunset Orange
//   "#1d3557", // Deep Navy
//   "#283618", // Forest Green
//   "#fefae0", // Warm Sand
//   "#457b9d", // Charcoal Blue
//   "#b5838d", // Soft Bronze
//   "#e29578", // Dusty Rose
//   "#83c5be", // Sage Green
//   "#e07a5f", // Desert Clay
//   "#3d5a80", // Slate
//   "#f4f1de", // Pale Gold
//   "#6b2d5c", // Vintage Burgundy
//   "#2b2d42"  // Evening Indigo
// ];

// Rich, high-vibe analog travel micro-labels
// const SAMPLE_LABELS = [
//   "Sunset Moments",
//   "Quiet Streets",
//   "Night Energy",
//   "Local Cafés",
//   "Hidden Corners",
//   "Ocean Breeze",
//   "Slow Mornings",
//   "Golden Hour",
//   "Local Flavors",
//   "Mountain Mist",
//   "Forest Paths",
//   "City Lights",
//   "Desert Trails",
//   "Cozy Nooks",
//   "Spontaneous Stops",
//   "Wanderlust",
//   "Memory Fragments",
//   "Undeveloped Memories",
//   "Living Atmosphere",
//   "Film Exposure"
// ];

export default function DayAtmosphereGallery({ city, activities = [], isMinimal = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [images, setImages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedVibe, setSelectedVibe] = useState("All");
  const [imageLoaded, setImageLoaded] = useState({});
  const [randomPage, setRandomPage] = useState(1);
  const hasFetched = useRef(false);

  // Initialize randomized memory teaser collage parameters once on mount
  // const [teaserStyle] = useState(() => {
  //   // Generate 4 abstract memory cards with organic overlapping placements
  //   const cards = Array.from({ length: 4 }).map((_, idx) => {
  //     const color1 = MUTED_TRAVEL_TONES[Math.floor(Math.random() * MUTED_TRAVEL_TONES.length)];
  //     let color2 = MUTED_TRAVEL_TONES[Math.floor(Math.random() * MUTED_TRAVEL_TONES.length)];
  //     while (color2 === color1) {
  //       color2 = MUTED_TRAVEL_TONES[Math.floor(Math.random() * MUTED_TRAVEL_TONES.length)];
  //     }

  //     // Distribute card position horizontally to fill the banner organically
  //     const leftDist = [12, 35, 58, 76];
  //     const left = leftDist[idx] + (Math.random() * 6 - 3);
  //     const top = -15 + Math.random() * 32; // Vertical offset
  //     const w = 110 + Math.random() * 70;   // width range
  //     const h = 140 + Math.random() * 80;   // height range
  //     const rotation = -15 + Math.random() * 30; // -15deg to 15deg
  //     const blur = 18 + Math.random() * 12; // 18px to 30px
  //     const floatDelay = -Math.random() * 20;

  //     return { color1, color2, left, top, w, h, rotation, blur, floatDelay, zIndex: (idx + 1) * 10 };
  //   });

  //   // Generate 3 subtle floating micro-labels
  //   const labels = Array.from({ length: 3 }).map((_, idx) => {
  //     const text = SAMPLE_LABELS[Math.floor(Math.random() * SAMPLE_LABELS.length)];
  //     const leftDist = [15, 50, 80];
  //     const left = leftDist[idx] + (Math.random() * 10 - 5);
  //     const top = 20 + Math.random() * 50; // Distributed vertically
  //     const floatDelay = -Math.random() * 20;

  //     return { text, left, top, floatDelay };
  //   });

  //   return { cards, labels };
  // });

  // Helper: dynamic query generator
  const getDynamicQuery = (targetCity, acts, vibe) => {
    if (vibe && vibe !== "All") {
      const vibeQueries = {
        Food: `${targetCity} culinary local food market restaurant cafe`,
        Nature: `${targetCity} moody scenic nature landscape green quiet`,
        Nightlife: `${targetCity} cinematic nightlife neon street moody light`,
        Culture: `${targetCity} authentic culture traditional temple architecture heritage`,
        Relaxed: `${targetCity} slow living aesthetic relaxed vibe calm morning`
      };
      return vibeQueries[vibe] || `${targetCity} atmospheric ${vibe}`;
    }

    // Default "All" - parse activities to discover the atmosphere
    const text = (acts || []).join(" ").toLowerCase();
    let subQuery = "moody travel photography aesthetic";

    if (text.includes("beach") || text.includes("sunset") || text.includes("ocean") || text.includes("sea") || text.includes("sand") || text.includes("coast")) {
      subQuery = "beach sunset scenic travel editorial";
    } else if (text.includes("food") || text.includes("dinner") || text.includes("restaurant") || text.includes("cafe") || text.includes("lunch") || text.includes("eat") || text.includes("bar")) {
      subQuery = "local food atmospheric cafe aesthetic dining";
    } else if (text.includes("night") || text.includes("neon") || text.includes("evening") || text.includes("nightlife")) {
      subQuery = "cinematic nightlife night lights moody street";
    } else if (text.includes("park") || text.includes("nature") || text.includes("mountain") || text.includes("hike") || text.includes("forest") || text.includes("lake") || text.includes("river")) {
      subQuery = "scenic moody nature landscape photography";
    } else if (text.includes("museum") || text.includes("temple") || text.includes("shrine") || text.includes("art") || text.includes("history") || text.includes("architecture")) {
      subQuery = "temple culture authentic history architecture";
    } else if (text.includes("shop") || text.includes("market") || text.includes("street") || text.includes("walk") || text.includes("explore")) {
      subQuery = "local street life authentic travel photography";
    }

    return `${targetCity} ${subQuery}`;
  };

  const fetchAtmosphere = async (vibe = selectedVibe, pageNum = randomPage) => {
    if (!city) return;
    setLoading(true);
    setError(false);
    setImageLoaded({});

    const query = getDynamicQuery(city, activities, vibe);
    const key = process.env.REACT_APP_UNSPLASH_KEY;

 if (!key) {
  console.warn(
    "Unsplash API Key (REACT_APP_UNSPLASH_KEY) is missing. Using premium atmospheric fallbacks."
  );

  // Premium cinematic loading timing
  setTimeout(() => {

    setImages(FALLBACK_IMAGES);

    // Extra smooth reveal delay
    setTimeout(() => {
      setLoading(false);
      hasFetched.current = true;
    }, 1800);

  }, 700);

  return;
}

    try {
      const res = await fetch(
        `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=4&orientation=landscape&page=${pageNum}`,
        {
          headers: {
            Authorization: `Client-ID ${key}`,
          },
        }
      );

      if (!res.ok) throw new Error("Unsplash API rate limit or error");
      const data = await res.json();

      if (data.results && data.results.length > 0) {
        setImages(data.results);
      } else {
        // If query too specific and returns nothing, search city moody aesthetic
        const fallbackRes = await fetch(
          `https://api.unsplash.com/search/photos?query=${encodeURIComponent(city + " moody travel")}&per_page=4&orientation=landscape`,
          {
            headers: {
              Authorization: `Client-ID ${key}`,
            },
          }
        );
        const fallbackData = await fallbackRes.json();
        if (fallbackData.results?.length > 0) {
          setImages(fallbackData.results);
        } else {
          setImages(FALLBACK_IMAGES);
        }
      }
    } catch (err) {
      console.error("Atmosphere fetch failed, playing fallback", err);
      setError(true);
      setImages(FALLBACK_IMAGES);
    } finally {

  // Premium cinematic loading timing
  setTimeout(() => {

    setLoading(false);
    hasFetched.current = true;

  }, 3200);

}
  };

  // Trigger lazy fetch only when expanded
  useEffect(() => {
  if (isExpanded && !hasFetched.current) {
    fetchAtmosphere(selectedVibe, randomPage);
  }

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [isExpanded]);

  // Handle vibe chip click
  const handleVibeClick = (vibe) => {
    setSelectedVibe(vibe);
    fetchAtmosphere(vibe, randomPage);
  };

  // Refresh Atmosphere
  const handleRefresh = (e) => {
    e.stopPropagation();
    const newPage = Math.floor(Math.random() * 5) + 1;
    setRandomPage(newPage);
    fetchAtmosphere(selectedVibe, newPage);
  };

  const handleImageLoad = (index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  };

  // Premium editorial visual copies
  // const getPremiumCopy = () => {
  //   if (!city) return "Explore the atmosphere →";
  //   const options = [
  //     `Experience ${city} visually →`,
  //     `Explore the atmosphere →`,
  //     `See the travel moments of this day →`,
  //     `Discover the vibe →`
  //   ];
  //   return options[(activities.length || 0) % options.length];
  // };

  return (
    <div className="w-full relative mt-8 z-10">
      
      {/* UPGRADED COLLAPSED "MEMORY TEASER" BANNER STATE */}
     {/* ===== NEW CLEAN PREMIUM COLLAPSED STATE ===== */}
<div
  onClick={() => setIsExpanded(true)}
  onMouseEnter={() => setIsHovered(true)}
  onMouseLeave={() => setIsHovered(false)}
  className={`
    relative overflow-hidden cursor-pointer
    rounded-[28px]
    border border-[#ece9df]
    bg-[#f8f7f3]
    px-5 py-5 sm:px-6 sm:py-6
    transition-all duration-500
    hover:shadow-[0_10px_35px_rgba(0,0,0,0.06)]
    hover:-translate-y-[2px]
    active:scale-[0.99]
    ${
      isExpanded
        ? "opacity-0 scale-95 pointer-events-none h-0"
        : "opacity-100 scale-100"
    }
  `}
>

  {/* SOFT BACKGROUND GLOW */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    
    <div className="
      absolute
      -top-10
      -left-10
      w-40
      h-40
      rounded-full
      bg-[#dce775]/20
      blur-3xl
    " />

    <div className="
      absolute
      bottom-0
      right-0
      w-32
      h-32
      rounded-full
      bg-[#c8d5b9]/30
      blur-3xl
    " />

  </div>

  {/* MAIN CONTENT */}
  <div className="relative z-10 flex items-center justify-between gap-5">

    {/* LEFT MEMORY COLLAGE */}
    <div className="relative w-[120px] h-[78px] flex-shrink-0">

      {/* CARD 1 */}
      <div
        className={`
          absolute
          left-0
          top-2
          w-[52px]
          h-[62px]
          rounded-2xl
          bg-gradient-to-br
          from-[#dce775]
          to-[#b7c957]
          blur-[1px]
          shadow-md
          rotate-[-8deg]
          transition-all duration-500
          ${isHovered ? "translate-y-[-2px]" : ""}
        `}
      />

      {/* CARD 2 */}
      <div
        className={`
          absolute
          left-[30px]
          top-0
          w-[56px]
          h-[66px]
          rounded-2xl
          bg-gradient-to-br
          from-[#f4d6a0]
          to-[#ddb892]
          blur-[1.5px]
          shadow-lg
          rotate-[6deg]
          transition-all duration-500
          ${isHovered ? "translate-y-[-4px]" : ""}
        `}
      />

      {/* CARD 3 */}
      <div
        className={`
          absolute
          left-[62px]
          top-[10px]
          w-[48px]
          h-[58px]
          rounded-2xl
          bg-gradient-to-br
          from-[#b7d3c0]
          to-[#84a98c]
          blur-[1px]
          shadow-md
          rotate-[12deg]
          transition-all duration-500
          ${isHovered ? "translate-y-[-2px]" : ""}
        `}
      />

      {/* CARD 4 */}
      <div
        className={`
          absolute
          left-[18px]
          top-[28px]
          w-[46px]
          h-[52px]
          rounded-2xl
          bg-gradient-to-br
          from-[#e9edc9]
          to-[#ccd5ae]
          blur-[2px]
          shadow-sm
          rotate-[2deg]
          transition-all duration-500
          ${isHovered ? "translate-y-[2px]" : ""}
        `}
      />

    </div>

    {/* RIGHT TEXT */}
    <div className="flex-1 min-w-0">

      <div className="
        text-[10px]
        uppercase
        tracking-[0.28em]
        text-[#7b8b5a]
        font-bold
        mb-2
      ">
        VISUAL ATMOSPHERE
      </div>

      <h3 className="
        text-[16px]
        sm:text-[18px]
        font-semibold
        text-[#1f2937]
        tracking-tight
        leading-snug
        transition-all
        duration-300
      ">
        Experience this day visually
        <span className="
          inline-block
          ml-2
          transition-transform
          duration-300
          group-hover:translate-x-1
        ">
          →
        </span>
      </h3>

      <p className="
        mt-1.5
        text-[13px]
        leading-relaxed
        text-[#6b7280]
        max-w-[260px]
      ">
        Curated cinematic visuals inspired by your itinerary and travel vibe.
      </p>

    </div>

  </div>

</div>

      {/* EXPANDED CONTENT WRAPPER - MORPH TRANSITION FADES AND GROWS */}
      <div
        style={{
          maxHeight: isExpanded ? "1200px" : "0px",
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? "scale(1) translateY(0)" : "scale(0.96) translateY(-10px)",
        }}
        className={`grid transition-all duration-[1s] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isExpanded ? "pointer-events-auto mt-6" : "pointer-events-none overflow-hidden"
        }`}
      >
        <div className="overflow-hidden">
          
          {/* Main Atmosphere Visual Layer */}
          <div className="bg-white/85 backdrop-blur-md rounded-[32px] p-5 sm:p-7 border border-gray-100/70 shadow-[0_12px_40px_-15px_rgba(0,0,0,0.04)] space-y-6">
            
            {/* Header controls & chips */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-100/50 pb-4">
              
              {/* Vibe Chips switcher */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 mr-2">Vibe:</span>
                {VIBE_CHIPS.map((vibe) => (
                  <button
                    key={vibe}
                    onClick={() => handleVibeClick(vibe)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 ${
                      selectedVibe === vibe
                        ? "bg-[#5b6f00] text-white shadow-sm shadow-[#5b6f00]/20"
                        : "bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    }`}
                  >
                    {vibe === "All" ? "✨ All Vibes" : vibe}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 ml-auto sm:ml-0">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-full text-xs font-bold transition-all active:scale-95 border border-gray-100 disabled:opacity-50"
                  title="Load alternative images"
                >
                  <span className={`inline-block ${loading ? "animate-spin" : ""} transition-transform duration-500`}>
                    ↻
                  </span>
                  Refresh atmosphere
                </button>

                {/* Elegant Close / Collapse Trigger inside expanded controls */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(false);
                    setIsHovered(false);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-full text-xs font-bold transition-all active:scale-95 border border-gray-100"
                  title="Collapse visual gallery"
                >
                  ✕ Close
                </button>
              </div>

            </div>

            {/* ERROR / RATE LIMIT BAR */}
            {error && (
              <div className="text-center py-2 px-4 bg-amber-50/50 border border-amber-100/50 rounded-xl text-xs text-amber-700 font-medium">
                ✧ Showing editorial atmosphere photography.
              </div>
            )}

            {/* SKELETON LOADER */}
            {loading ? (

  <div className="relative overflow-hidden rounded-[30px] border border-[#ece9df] bg-[#f8f7f3] p-8">

    {/* SOFT GLOW */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">

      <div className="
        absolute
        -top-10
        -left-10
        w-40
        h-40
        rounded-full
        bg-[#dce775]/20
        blur-3xl
      " />

      <div className="
        absolute
        bottom-0
        right-0
        w-32
        h-32
        rounded-full
        bg-[#c8d5b9]/30
        blur-3xl
      " />

    </div>

    {/* CONTENT */}
    <div className="relative z-10 flex flex-col items-center justify-center py-10">

      <MiniLoader size={26} />

      <div className="mt-5 text-center">

        <p className="
          text-[14px]
          font-semibold
          tracking-wide
          text-[#5b6f00]
        ">
          Curating your atmosphere
        </p>

        <p className="
          mt-1
          text-[12px]
          text-[#6b7280]
          leading-relaxed
        ">
          Finding cinematic travel moments inspired by your itinerary...
        </p>

      </div>

      {/* PREMIUM FAKE MEMORY COLLAGE */}
      <div className="mt-8 flex items-center justify-center gap-3">

        <div className="
          w-[58px]
          h-[72px]
          rounded-2xl
          bg-gradient-to-br
          from-[#dce775]
          to-[#b7c957]
          blur-[1px]
          rotate-[-8deg]
          opacity-80
          animate-pulse
        " />

        <div className="
          w-[68px]
          h-[82px]
          rounded-2xl
          bg-gradient-to-br
          from-[#f4d6a0]
          to-[#ddb892]
          blur-[1px]
          rotate-[6deg]
          opacity-90
          animate-pulse
        " />

        <div className="
          w-[54px]
          h-[68px]
          rounded-2xl
          bg-gradient-to-br
          from-[#b7d3c0]
          to-[#84a98c]
          blur-[1px]
          rotate-[10deg]
          opacity-80
          animate-pulse
        " />

      </div>

    </div>

  </div>

) : (
              /* REAL IMAGES VIEW */
              <div className="w-full">
                {isMinimal ? (
                  /* HOURLY MINIMAL GRID */
                  <div className="grid grid-cols-3 gap-3 h-28 sm:h-32">
                    {images?.slice(0, 3).map((img, idx) => (
                      <div key={idx} className="group/minimal relative rounded-2xl overflow-hidden shadow-sm bg-gray-50 border border-gray-100/50">
                        {/* Shimmer loading background while image loads */}
                        {!imageLoaded[idx] && (
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 animate-pulse" />
                        )}
                        <img
                          src={img.urls.small}
                          alt={img.alt_description || "Atmospheric moment"}
                          className={`w-full h-full object-cover transition-all duration-1000 ease-out group-hover/minimal:scale-105 ${
                            imageLoaded[idx] ? "opacity-100 scale-100" : "opacity-0 scale-95"
                          }`}
                          onLoad={() => handleImageLoad(idx)}
                        />
                        <div className="absolute inset-0 bg-black/5 opacity-80 group-hover/minimal:bg-black/0 transition-all duration-300" />
                      </div>
                    ))}
                  </div>
                ) : (
                  /* LUXURY MAGAZINE EDITORIAL LAYOUT */
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 h-auto md:h-[430px]">
                    
                    {/* Primary Hero Image - Left (Takes 7/12 on Desktop) */}
                    {images?.[0] && (
                      <div className="group/hero md:col-span-7 h-[240px] md:h-full relative overflow-hidden rounded-[24px] shadow-sm hover:shadow-md transition-all duration-500 bg-gray-50 border border-gray-100/50">
                        {!imageLoaded[0] && (
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 animate-pulse" />
                        )}
                        <img
                          src={images[0].urls.regular}
                          alt={images[0].alt_description || "Cinematic travel atmosphere"}
                          className={`w-full h-full object-cover transition-all duration-[1.5s] ease-out group-hover/hero:scale-105 ${
                            imageLoaded[0] ? "opacity-100 scale-100" : "opacity-0 scale-95"
                          }`}
                          onLoad={() => handleImageLoad(0)}
                        />
                        {/* Atmospheric soft overlay & typography caption */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 text-left">
                          <span className="text-[10px] uppercase font-bold tracking-[0.2em] bg-white/25 text-white backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10">
                            Atmosphere in {city}
                          </span>
                          <h4 className="text-white text-lg font-bold tracking-tight mt-2.5 drop-shadow-sm line-clamp-1">
                            {images[0].alt_description ? images[0].alt_description.charAt(0).toUpperCase() + images[0].alt_description.slice(1) : `${city} Visual Moment`}
                          </h4>
                          {images[0].user?.name && (
                            <p className="text-white/60 text-[9px] font-medium tracking-wider uppercase mt-1">
                              Captured by {images[0].user.name}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Supporting Editorial Column - Right (Takes 5/12 on Desktop) */}
                    <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-4 h-auto md:h-full">
                      
                      {/* Top Supporting image */}
                      {images?.[1] && (
                        <div className="group/sup1 flex-1 h-[140px] md:h-[calc(50%-8px)] relative overflow-hidden rounded-[20px] shadow-sm hover:shadow-md transition-all duration-500 bg-gray-50 border border-gray-100/50">
                          {!imageLoaded[1] && (
                            <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 animate-pulse" />
                          )}
                          <img
                            src={images[1].urls.regular}
                            alt={images[1].alt_description || "Supporting vibe"}
                            className={`w-full h-full object-cover transition-all duration-[1.2s] ease-out group-hover/sup1:scale-105 ${
                              imageLoaded[1] ? "opacity-100 scale-100" : "opacity-0 scale-95"
                            }`}
                            onLoad={() => handleImageLoad(1)}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-80" />
                          <div className="absolute bottom-4 left-4 right-4 text-left">
                            <p className="text-white/70 text-[9px] font-bold uppercase tracking-widest">
                              {selectedVibe === "All" ? "Vibe Discover" : selectedVibe}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Bottom Supporting image */}
                      {images?.[2] && (
                        <div className="group/sup2 flex-1 h-[140px] md:h-[calc(50%-8px)] relative overflow-hidden rounded-[20px] shadow-sm hover:shadow-md transition-all duration-500 bg-gray-50 border border-gray-100/50">
                          {!imageLoaded[2] && (
                            <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 animate-pulse" />
                          )}
                          <img
                            src={images[2].urls.regular}
                            alt={images[2].alt_description || "Supporting vibe details"}
                            className={`w-full h-full object-cover transition-all duration-[1.2s] ease-out group-hover/sup2:scale-105 ${
                              imageLoaded[2] ? "opacity-100 scale-100" : "opacity-0 scale-95"
                            }`}
                            onLoad={() => handleImageLoad(2)}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-80" />
                          <div className="absolute bottom-4 left-4 right-4 text-left">
                            <p className="text-white/70 text-[9px] font-bold uppercase tracking-widest">
                              Travel Details
                            </p>
                          </div>
                        </div>
                      )}

                    </div>

                  </div>
                )}
              </div>
            )}

            {/* Small subtle footer in expanded state */}
            <div className="flex justify-between items-center text-[9px] font-semibold uppercase tracking-wider text-gray-400 pt-2 border-t border-gray-100/40">
              <span>Dynamic Travel Curation</span>
              <span>Images Powered by Unsplash</span>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
}
